import type {RouteLocationNormalizedLoaded, RouteParamValue} from 'vue-router'

type CanonicalRoute = Pick<RouteLocationNormalizedLoaded, 'path' | 'params' | 'matched'>

const PARAM_RE = /^:([^(/?+*]*)(?:\([^)]*\))?([?+*]*)$/

/**
 * Собирает относительный canonical из шаблона совпавшего маршрута.
 * Статические сегменты и обязательные параметры остаются, необязательные (`?`) отбрасываются.
 * Query и hash в шаблон не входят. Catch-all (404) не укорачивается.
 */
export function buildCanonicalUrl(route: CanonicalRoute): string {
    const fallback = normalizePath(route.path)
    const pattern = routePattern(route.matched)
    if (!pattern || isNotFound(route, pattern)) return fallback
    return buildFromPattern(pattern, route.params, fallback)
}

function routePattern(matched: CanonicalRoute['matched']): string {
    let pattern = ''
    for (const record of matched) {
        const path = record.path || ''
        if (!path) continue
        if (path.startsWith('/')) pattern = path
        else pattern = `${pattern.replace(/\/$/, '')}/${path}`
    }
    return pattern || '/'
}

function isNotFound(route: CanonicalRoute, pattern: string): boolean {
    if (route.matched.some((record) => record.meta?.ssrStatus === 404)) return true
    return pattern.split('/').some((segment) => {
        const param = parseParam(segment)
        return param?.splat === true
    })
}

function buildFromPattern(
    pattern: string,
    params: CanonicalRoute['params'],
    fallback: string,
): string {
    const trailingSlash = pattern.length > 1 && pattern.endsWith('/')
    const out: string[] = []

    for (const segment of pattern.split('/')) {
        if (!segment) continue
        const param = parseParam(segment)
        if (!param) {
            out.push(segment)
            continue
        }
        if (param.optional) continue
        const parts = paramParts(params[param.name])
        if (!parts) return fallback
        out.push(...parts)
    }

    const path = `/${out.join('/')}`
    return trailingSlash ? `${path}/` : path
}

function parseParam(segment: string): {optional: boolean, splat: boolean, name: string} | null {
    const match = PARAM_RE.exec(segment)
    if (!match) return null
    const modifier = match[2] || ''
    return {
        name: match[1],
        optional: modifier.includes('?'),
        splat: modifier.includes('*'),
    }
}

function paramParts(value: RouteParamValue | RouteParamValue[] | undefined): string[] | null {
    if (value == null || value === '') return null
    const parts = (Array.isArray(value) ? value : [value]).filter((part) => part !== '')
    if (!parts.length) return null
    return parts.map((part) => encodeURIComponent(part))
}

function normalizePath(path: string): string {
    const pathname = (path || '/').split(/[?#]/)[0] || '/'
    return pathname.startsWith('/') ? pathname : `/${pathname}`
}
